import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../App';
import { io } from 'socket.io-client';
import { apiOrigin } from '../apiOrigin';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Database,
  Eye,
  FileSearch,
  Fingerprint,
  ListChecks,
  LockKeyhole,
  MapPin,
  Radio,
  Save,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  UserCheck,
} from 'lucide-react';
import './LocationTrust.css';
import PageHeader from '../components/PageHeader';
import { datumUndZeit } from '../lib/zeit';

const COLORS = { allow: '#16a34a', review: '#d97706', confirmed: '#dc2626', dismissed: '#16a34a' };
const fmt = (ms) => (ms ? datumUndZeit(Number(ms), { jahr: 'voll', sekunden: true, leer: '–' }) : '–');

// ATTESTIERUNG-GRUND-ZEIGEN-001: Der Server liefert den Ablehnungsgrund (`reason`) im
// providers-Objekt längst mit — er wurde nur nie angezeigt. Die Meldung nennt die Angabe
// und ihr Sollformat, niemals einen konfigurierten Wert.
const PROVIDER_REASON_HINTS = {
  android: {
    provider_not_configured: 'Es fehlen Play-Integrity-Angaben: das Google-Dienstkonto (Schlüsseldatei), je ein Paketname und je ein Zertifikats-Fingerabdruck für Fahrer- und Kunden-App.',
    provider_configuration_invalid: 'Eine Play-Integrity-Angabe hat das falsche Format. Häufigste Ursache: Der Zertifikats-Fingerabdruck ist mit Doppelpunkten eingetragen — verlangt sind genau 64 Zeichen 0–9a–f ohne Doppelpunkte, klein geschrieben (die Play Console zeigt ihn MIT Doppelpunkten an). Außerdem: Paketnamen im Format wie de.voogo.app, Fahrer- und Kunden-App verschieden, und das Dienstkonto als vollständige JSON-Schlüsseldatei mit E-Mail und RSA-Schlüssel ab 2048 Bit.',
    physical_e2e_required: 'Die Angaben sind vollständig und gültig — es fehlt nur die bestätigte End-to-End-Prüfung beider echter Apps (Fahrer und Kunde) innerhalb der letzten 24 Stunden.',
  },
  ios: {
    provider_not_configured: 'Es fehlen App-Attest-Angaben: Team-ID, App-ID-Präfix, das Apple-Root-Zertifikat (PEM) und je eine Bundle-ID für Fahrer- und Kunden-App.',
    provider_configuration_invalid: 'Eine App-Attest-Angabe hat das falsche Format. Team-ID und App-ID-Präfix bestehen aus genau 10 Zeichen A–Z/0–9, Bundle-IDs im Format wie de.voogo.app (Fahrer- und Kunden-App verschieden), und das Root-Zertifikat muss ein CA-Zertifikat im PEM-Format sein.',
    physical_e2e_required: 'Die Angaben sind vollständig und gültig — es fehlt nur die bestätigte End-to-End-Prüfung beider echter Apps (Fahrer und Kunde) innerhalb der letzten 24 Stunden.',
  },
};
const providerBlockText = (platform, provider) => `${platform === 'android' ? 'Android' : 'iOS'}: ${
  PROVIDER_REASON_HINTS[platform]?.[provider?.reason]
  || 'Die Provider-Konfiguration ist unvollständig oder ungültig.'}`;

// ATTESTIERUNG-VERSTAENDLICH-001 — Die Seite beschriftete ihre vier Auswahlfelder mit den
// Variablennamen aus dem Code und ließ jeden gesperrten Schalter wortlos grau. Die
// Verriegelung ist richtig und bleibt Zeile für Zeile unverändert; sie bekommt hier nur eine
// Sprache. Jeder Satz beschreibt GEMESSENES Verhalten, keine Absicht — Belege stehen dabei.
const MODE_FIELDS = [
  {
    field: 'driver_mode',
    platform: null,
    label: 'Standortprüfung der Fahrer-App',
    effect: 'Ist das an, bewertet der Server die Standortmeldungen der Fahrer-App bei '
      + 'Fahrt-Aktionen (Ankunft, Abschluss) und legt daraus die Evidenzfälle oben in dieser Liste an.',
  },
  {
    field: 'customer_mode',
    platform: null,
    label: 'Standortprüfung der Kunden-App',
    effect: 'Ist das an, bewertet der Server dieselben Standortmeldungen für die Kunden-App '
      + '(z. B. beim Bestätigen der Abholung) und legt daraus Evidenzfälle an.',
  },
  {
    field: 'android_attestation_mode',
    platform: 'android',
    label: 'Echtheitsprüfung der Android-Apps',
    effect: 'Ist das an, muss jede Android-Anfrage bei Google nachweisen, dass sie aus der '
      + 'echten, unveränderten VOOGO-App kommt (Play Integrity). Genau das trifft die gefälschte App.',
  },
  {
    field: 'ios_attestation_mode',
    platform: 'ios',
    label: 'Echtheitsprüfung der iOS-Apps',
    effect: 'Ist das an, muss jede iPhone-Anfrage bei Apple nachweisen, dass sie aus der '
      + 'echten, unveränderten VOOGO-App kommt (App Attest).',
  },
];

// Gemessen, nicht neu erfunden: `observe` → `not_evaluated` (lib/locationTrust.js:673);
// `shadow` steht in `shadowModes` und weist deshalb nie ab (:777); `enforce` weist
// ausschließlich nachweislichen Durchfall ab (ATTESTIERUNG-TUERSTEHER-001).
const MODE_GLOSSARY = [
  { mode: 'off', text: 'Aus. Es passiert nichts.' },
  { mode: 'observe', text: 'Die Prüfung läuft mit und wird festgehalten, aber nicht bewertet.' },
  { mode: 'shadow', text: 'Die Prüfung wird voll bewertet, jeder Fall bekommt ein Urteil — abgewiesen wird niemand.' },
  { mode: 'enforce', text: 'Dasselbe Urteil, und eine nachweislich durchgefallene App wird abgewiesen. '
    + '„Nicht prüfbar" (Anbieter nicht erreichbar, ältere App) kommt durch und wird protokolliert.' },
];

const platformName = (platform) => (platform === 'android' ? 'Android' : 'iOS');
const stepState = (done) => (done ? 'erledigt' : 'offen');
// Schritt 3 und 4 gelten je Plattform. „teilweise" ist die ehrliche Antwort, wenn eine
// Plattform steht und die andere nicht — ein einzelnes Häkchen würde eine der beiden verschweigen.
const platformStepState = (providers, key) => {
  const android = providers?.android?.[key] === true;
  const ios = providers?.ios?.[key] === true;
  const label = `Android: ${stepState(android)} · iOS: ${stepState(ios)}`;
  return { done: android && ios, partial: android !== ios, label };
};

// Die Reihenfolge ist nicht erfunden, sondern die des Servers:
// keine Betriebsart ohne Retention (admin.js → `location_retention_required`), keine Retention
// ohne Freigabe (`retention_legal_approval_required`), keine Attestierung ohne hinterlegte
// Anbieter-Werte (`attestation_provider_not_configured`), kein `enforce` ohne frischen
// Zwei-Geräte-Test (`attestation_provider_not_ready`).
const configPath = (config, retentionApproval, providers) => {
  const approvalDone = !!retentionApproval;
  const retentionDone = config?.retention_enabled === true;
  const provider = platformStepState(providers, 'activation_ready');
  const e2e = platformStepState(providers, 'ready');
  return [
    {
      key: 'approval',
      title: 'Retention-Freigabe anlegen',
      unlocks: 'schaltet den Retention-Schalter frei',
      detail: 'Aktenzeichen, Freigabedatum, Begründung und Häkchen — im Formular „Retention-Freigabe '
        + 'auditiert erfassen" weiter unten auf dieser Seite.',
      who: 'Das machen Sie selbst.',
      done: approvalDone,
      state: approvalDone ? 'erledigt' : 'offen',
    },
    {
      key: 'retention',
      title: 'Retention einschalten',
      unlocks: 'schaltet alle vier Auswahlfelder frei (observe / shadow)',
      detail: 'Der Schalter „Aufbewahrung (Retention)" unten, danach speichern. Ohne ihn weist der '
        + 'Server jede Betriebsart außer „off" ab — deshalb ist oben alles grau.',
      who: 'Das machen Sie selbst.',
      done: retentionDone,
      state: retentionDone ? 'erledigt' : 'offen',
    },
    {
      key: 'provider',
      title: 'Anbieter-Werte auf dem Server hinterlegen',
      unlocks: 'schaltet die beiden Echtheitsprüfungen frei (observe / shadow)',
      detail: 'Play-Integrity- bzw. App-Attest-Angaben. Sie liegen nicht in dieser Oberfläche, '
        + 'sondern in der Server-Konfiguration — welche Angabe fehlt, steht unten bei „Attestation-Provider".',
      who: 'Das machen wir am Server, nicht Sie auf dieser Seite.',
      done: provider.done,
      state: provider.done ? 'erledigt' : provider.partial ? 'teilweise' : 'offen',
      perPlatform: provider.label,
    },
    {
      key: 'e2e',
      title: 'Frischer Zwei-Geräte-Test',
      unlocks: 'schaltet „enforce" frei',
      detail: 'Beide echten Apps — Fahrer und Kunde — müssen die Prüfung mit der aktuellen '
        + 'Konfiguration innerhalb der letzten 24 Stunden bestanden haben. Das Einschalten von enforce '
        + 'braucht danach zusätzlich Ihr Häkchen und eine Begründung.',
      who: 'Gemeinsam: wir stellen bereit, Sie fahren den Test auf zwei echten Geräten.',
      done: e2e.done,
      state: e2e.done ? 'erledigt' : e2e.partial ? 'teilweise' : 'offen',
      perPlatform: e2e.label,
    },
  ];
};

// Warum ist GENAU DIESES Feld gesperrt — und was kommt als Nächstes? Die Reihenfolge der
// Prüfungen ist dieselbe wie in `modeUnavailable` unten; hier wird sie nur ausgesprochen.
const modeLockText = (platform, config, retentionApproval, providers) => {
  if (config?.retention_enabled !== true) {
    return retentionApproval
      ? 'Gesperrt, weil die Aufbewahrung (Retention) aus ist. Nächster Schritt 2: den Schalter '
        + '„Aufbewahrung (Retention)" unten einschalten und speichern. Die Freigabe dafür liegt bereits vor.'
      : 'Gesperrt, weil die Aufbewahrung (Retention) aus ist und dafür noch keine Freigabe vorliegt. '
        + 'Nächster Schritt 1: unten „Retention-Freigabe auditiert erfassen" ausfüllen.';
  }
  if (platform && providers?.[platform]?.activation_ready !== true) {
    return `Gesperrt, weil die Anbieter-Werte fehlen. ${providerBlockText(platform, providers?.[platform])} `
      + 'Nächster Schritt 3: Das hinterlegen wir am Server — auf dieser Seite ist dafür nichts einzustellen.';
  }
  return null;
};

const enforceLockText = (platform) => 'Nur „enforce" ist noch gesperrt. Es fehlt Schritt 4: der frische '
  + `Zwei-Geräte-Test für ${platformName(platform)} — beide echten Apps (Fahrer und Kunde) müssen die Prüfung `
  + 'mit der aktuellen Konfiguration innerhalb der letzten 24 Stunden bestanden haben. Danach braucht das '
  + 'Einschalten zusätzlich Ihr Häkchen und eine Begründung ganz unten.';

// Diese Gründe liefert der Server längst mit (`test_enforcement.blockers`) — angezeigt wurden
// sie nie. Nur die Struktur wird übersetzt, kein konfigurierter Wert wird ausgegeben.
const TEST_ENFORCEMENT_BLOCKER_TEXT = {
  retention_approval_required: 'Retention ist aus oder es fehlt die Freigabe (Schritt 1 und 2).',
  location_shadow_required: 'Beide Standortprüfungen — Fahrer-App UND Kunden-App — müssen auf „shadow" stehen.',
  android_attestation_shadow_required: 'Die Echtheitsprüfung der Android-Apps muss auf „shadow" stehen.',
  android_provider_physical_e2e_required: 'Der frische Zwei-Geräte-Test für Android fehlt (Schritt 4).',
  test_device_allowlist_required: 'Es ist noch kein Testgerät zugelassen — die Geräteliste ist leer.',
};

// ─────────────────────────────────────────────────────────────────────────────────────────
// ATTESTIERUNG-WAS-PRUEFE-ICH-001 — Die Prüfliste zeigte die INNEREN NAMEN des Systems
// („cancel", „matching", „mock_reported") statt Fragen an einen Menschen. Gemessen: der
// Server liefert alles Nötige längst mit, es wurde nur nie gezeigt — reine Anzeige, keine
// Backend-Ergänzung. Jede Übersetzung unten ist am Code belegt, nichts ist geraten.
// ─────────────────────────────────────────────────────────────────────────────────────────

// Aktionen: die 12 Aufrufstellen in `routes/rides.js` + die Stufenliste in `locationPolicy.js:6–12`.
const ACTION_TEXT = {
  accept: 'Fahrt angenommen',
  matching: 'Fahrt wurde zugeteilt',
  readiness: 'Fahrer meldet sich bereit',
  online_readiness: 'Fahrer geht online',
  arrived: 'Ankunft beim Kunden gemeldet',
  in_progress: 'Fahrt gestartet',
  start: 'Fahrt gestartet',
  completed: 'Fahrt abgeschlossen',
  complete: 'Fahrt abgeschlossen',
  cancel: 'Fahrt storniert',
  cancelled: 'Fahrt storniert',
  no_show: 'Kunde nicht erschienen gemeldet',
  update_route: 'Route während der Fahrt geändert',
  stop_arrived: 'Ankunft an einem Zwischenstopp',
  stop_depart: 'Weiterfahrt von einem Zwischenstopp',
  stop_wait: 'Wartezeit am Zwischenstopp',
  paid_wait: 'Kostenpflichtige Wartezeit',
  reservation_claim: 'Vorbestellung übernommen',
  reservation_bundle_accept: 'Vorbestellungs-Paket angenommen',
  reservation_start: 'Vorbestellung gestartet',
};
const actionText = (action) => ACTION_TEXT[action] || `Aktion „${action}"`;

// Gründe: `classifyPoint` (:64–105) und `evaluateAction` (:340–438). Jeder Satz sagt, was
// beobachtet wurde — nicht, wer schuld ist. Der Owner entscheidet, nicht der Text.
const REASON_TEXT = {
  mock_reported: 'Das Gerät hat selbst gemeldet, dass der Standort vorgetäuscht ist (Mock-Location).',
  simulated_by_software: 'Die Position kam von einer Software, nicht von einem echten Sensor.',
  simulator_build: 'Die App lief in einem Simulator, nicht auf einem echten Gerät.',
  production_simulator_not_allowlisted: 'Ein Simulator lief gegen den Echtbetrieb, ohne dafür zugelassen zu sein.',
  test_session_nonproduction: 'Die Sitzung lief in einer Testumgebung, nicht im Echtbetrieb.',
  test_session_allowlisted: 'Simulator, aber ausdrücklich als Testgerät zugelassen.',
  root_detected: 'Das Android-Gerät ist gerootet.',
  jailbreak_detected: 'Das iPhone ist jailbroken.',
  jailbreak_suspected: 'Es gibt Anzeichen für einen Jailbreak.',
  instrumentation_detected: 'Die App wurde von außen manipuliert (Instrumentierung erkannt).',
  device_compromise_suspected: 'Das Gerät wirkt kompromittiert.',
  suspicious_source_evidence: 'Mindestens ein verwendeter Standortpunkt kam aus einer verdächtigen Quelle.',
  implausible_speed: 'Zwischen zwei Punkten lag eine Geschwindigkeit, die niemand fahren kann.',
  accuracy_too_poor: 'Die Standortgenauigkeit war zu schlecht, um darauf etwas zu stützen.',
  accuracy_degraded: 'Die Standortgenauigkeit war schwach, aber noch brauchbar.',
  degraded_accuracy_evidence: 'Mindestens ein verwendeter Punkt war ungenau.',
  degraded_accuracy_requires_multipoint: 'Bei dieser Genauigkeit hätte es mehr als einen Punkt gebraucht.',
  stale_client_time: 'Die Uhrzeit des Geräts war zu alt.',
  future_client_time: 'Die Uhrzeit des Geräts lag in der Zukunft.',
  last_point_stale: 'Der letzte Standortpunkt war zum Zeitpunkt der Aktion schon zu alt.',
  insufficient_points: 'Es lagen zu wenige brauchbare Standortpunkte vor.',
  multipoint_time_span_insufficient: 'Die Punkte lagen zeitlich zu dicht beieinander, um etwas zu belegen.',
  parallel_device_session: 'Dasselbe Konto war gleichzeitig auf einem zweiten Gerät aktiv.',
  device_session_rotated: 'Das Gerät hat mitten im Vorgang die Sitzung gewechselt.',
  missing_or_expired_session: 'Zur Aktion gab es keine gültige, offene Gerätesitzung.',
  actor_mismatch: 'Die Gerätesitzung gehörte zu jemand anderem als dem Handelnden.',
  ride_mismatch: 'Die Gerätesitzung gehörte zu einer anderen Fahrt.',
  action_evidence_mismatch: 'Der mitgeschickte Standortbeleg passte nicht zum zuletzt gemeldeten Punkt.',
  action_sequence_not_high_water: 'Die Nummerierung der Standortmeldungen passte nicht — ein Zeichen für Wiedereinspielung.',
  action_target_missing: 'Zu dieser Aktion gab es keinen Zielort, gegen den geprüft werden konnte.',
  pickup_proximity_missing: 'Der Fahrer war beim Abholort nicht nah genug dran.',
  dropoff_proximity_missing: 'Der Fahrer war beim Zielort nicht nah genug dran.',
  dropoff_proximity_or_route_progress_missing: 'Weder Nähe zum Ziel noch ein plausibler Streckenverlauf waren belegt.',
  stop_proximity_missing: 'Der Fahrer war am Zwischenstopp nicht nah genug dran.',
  server_route_progress_90: 'Der Streckenverlauf war zu über 90 % gefahren (das spricht dafür, nicht dagegen).',
  server_wait_window_incomplete: 'Die vorgeschriebene Wartezeit war noch nicht abgelaufen.',
  wait_window_evidence_incomplete: 'Für das Wartefenster fehlten Belegpunkte (Anfang, Mitte, Ende).',
  legacy_v1: 'Die App meldete noch im alten Format ohne signierten Umschlag.',
  mode_off: 'Die Standortprüfung war zu diesem Zeitpunkt ausgeschaltet.',
  attestation_unverified: 'Die Echtheit der App war nicht bestätigt.',
  test_device_header_missing: 'Dem Testgerät fehlte die Gerätekennung im Aufruf.',
  test_device_session_missing: 'Dem Testgerät fehlte die Sitzungskennung im Aufruf.',
  test_device_session_mismatch: 'Die angegebene Sitzung gehört nicht zu diesem Testgerät.',
  test_device_installation_mismatch: 'Die Geräteinstallation passt nicht zur zugelassenen Liste.',
  test_device_actor_mismatch: 'Die Testgerät-Sitzung gehört zu einem anderen Konto.',
};
const reasonText = (reason) => REASON_TEXT[reason]
  || `Weiterer Hinweis des Systems: „${reason}" — hier noch nicht in Klartext übersetzt.`;

// Echtheit der App: `attestation_state`/`attestation_reason` kommen mit, wurden nie gezeigt.
const ATTESTATION_STATE_TEXT = {
  verified: { tone: 'ok', text: 'Die App hat sich als echt und unverändert ausgewiesen.' },
  off: { tone: 'neutral', text: 'Die Echtheitsprüfung war für diese Aktion nicht eingeschaltet.' },
  unavailable: { tone: 'neutral', text: 'Die Echtheit war nicht prüfbar — der Anbieter war nicht erreichbar. Das gilt ausdrücklich NICHT als Durchfall.' },
  failed: { tone: 'alarm', text: 'Die App ist bei der Echtheitsprüfung durchgefallen.' },
  missing: { tone: 'warn', text: 'Die App hat gar keinen Echtheitsnachweis mitgeschickt.' },
};
const ATTESTATION_REASON_TEXT = {
  provider_not_configured: 'Für diese Plattform waren keine Anbieter-Werte hinterlegt.',
  provider_config_unavailable: 'Die Anbieter-Einstellung war im Moment der Aktion nicht lesbar.',
  provider_error: 'Der Anbieter hat mit einem Fehler geantwortet.',
  provider_timeout: 'Der Anbieter hat nicht rechtzeitig geantwortet.',
  provider_failed: 'Der Anbieter hat die App abgelehnt.',
  provider_contract_invalid: 'Die Antwort des Anbieters passte nicht zum erwarteten Vertrag.',
  provider_unclassified: 'Der Anbieter meldete einen Grund, den wir nicht einordnen konnten.',
  attestation_headers_missing: 'Dem Aufruf fehlten die Felder für den Echtheitsnachweis.',
  unknown_challenge: 'Der Nachweis bezog sich auf eine Aufgabe, die der Server nie gestellt hat.',
  challenge_replayed: 'Derselbe Nachweis wurde ein zweites Mal eingereicht.',
  challenge_expired: 'Der Nachweis kam zu spät.',
  challenge_actor_mismatch: 'Der Nachweis gehörte zu einem anderen Konto.',
  challenge_device_mismatch: 'Der Nachweis gehörte zu einem anderen Gerät.',
  challenge_action_mismatch: 'Der Nachweis gehörte zu einer anderen Aktion.',
  challenge_ride_mismatch: 'Der Nachweis gehörte zu einer anderen Fahrt.',
  request_hash_mismatch: 'Der Inhalt des Aufrufs stimmte nicht mit dem überein, was unterschrieben wurde.',
  request_hash_header_mismatch: 'Die Unterschrift im Kopf des Aufrufs passte nicht zum Inhalt.',
  counter_replay: 'Der Zähler des Geräteschlüssels lief rückwärts — ein Zeichen für Wiedereinspielung.',
  counter_missing: 'Dem Nachweis fehlte der Zähler des Geräteschlüssels.',
  key_actor_mismatch: 'Der Geräteschlüssel gehört zu einem anderen Konto.',
  key_device_mismatch: 'Der Geräteschlüssel gehört zu einem anderen Gerät.',
  invalid_key_registration: 'Der Geräteschlüssel war nicht gültig registriert.',
  device_session_unknown: 'Die Gerätesitzung war dem Server unbekannt.',
  device_session_missing: 'Es wurde keine Gerätesitzung mitgeschickt.',
  device_session_actor_or_state_mismatch: 'Die Gerätesitzung passte nicht zum Konto oder war nicht mehr offen.',
  attestation_platform_session_mismatch: 'Die angegebene Plattform passte nicht zur Gerätesitzung.',
  attestation_device_session_mismatch: 'Der Nachweis passte nicht zur Gerätesitzung.',
  attestation_action_invalid: 'Die Aktion im Nachweis war nicht zulässig.',
  attestation_observer_unavailable: 'Die Echtheitsprüfung selbst war vorübergehend nicht verfügbar.',
};

// Was heißt das Fall-Urteil (`locationTrust.js:438`)?
const CASE_VERDICT = {
  review: { tone: 'warn', label: 'Zur Prüfung', text: 'Mindestens eine Bedingung war nicht erfüllt — deshalb liegt der Fall Ihnen vor.' },
  allow: { tone: 'ok', label: 'Unauffällig', text: 'Alle Bedingungen waren erfüllt. Hier ist nichts zu entscheiden; die Zeile steht nur zur Nachvollziehbarkeit da.' },
  not_evaluated: { tone: 'neutral', label: 'Nur mitgeschrieben', text: 'Die Betriebsart stand auf „observe": mitgeschrieben, aber bewusst nicht bewertet.' },
  disabled: { tone: 'neutral', label: 'Abgeschaltet', text: 'Die Standortprüfung war zu diesem Zeitpunkt aus.' },
  untrusted: { tone: 'warn', label: 'Nicht belastbar', text: 'Die Meldung war nicht als Beleg verwendbar.' },
  unavailable: { tone: 'neutral', label: 'Nicht prüfbar', text: 'Die Prüfung konnte technisch nicht durchgeführt werden.' },
  fallback: { tone: 'neutral', label: 'Über Ersatzweg', text: 'Der Vorgang wurde über einen Ersatznachweis abgesichert (z. B. bestätigte PIN).' },
};
const caseVerdict = (decision) => CASE_VERDICT[decision]
  || { tone: 'neutral', label: decision || 'offen', text: 'Zu diesem Urteil liegt keine Erklärung vor.' };

// Was der Owner mit einem Klick auslöst — GEMESSEN, wer `location_review_decisions` liest:
// `confirmed`/`dismissed` liest niemand (reines Protokoll); nur `support_override` und
// `degraded_override` wirken, und zwar über `resolveFallback` (locationTrust.js:449–455)
// ausschließlich dort, wo überhaupt abgewiesen wird. Der Türsteher gegen gefälschte Apps
// (`attestationEnforcementDecision`, :546) fragt den Ersatzweg NIE — deshalb der letzte Satz.
const DECISION_EFFECT = [
  {
    key: 'confirmed',
    label: 'Auffälligkeit bestätigen',
    effect: 'Hält fest, dass die Auffälligkeit echt war. **Die Fahrt selbst ändert sich dadurch nicht** — '
      + 'sie ist längst gelaufen. Es wird niemand gesperrt und kein Geld bewegt. Der Eintrag ist ein '
      + 'auditiertes Protokoll für spätere Fragen.',
  },
  {
    key: 'dismissed',
    label: 'Verwerfen',
    effect: 'Hält fest, dass es ein Fehlalarm war. Ebenfalls **ohne Wirkung auf Fahrt, Fahrer oder Geld** — '
      + 'nur die Zeile wird als erledigt geführt.',
  },
  {
    key: 'support_override',
    label: 'Support-Override',
    effect: 'Das einzige mit echter Wirkung: Bei **gleichartigen Aktionen derselben Fahrt und desselben '
      + 'Handelnden** gilt der Fall künftig als von Hand freigegeben. Spürbar wird das nur dort, wo '
      + 'überhaupt abgewiesen wird — heute allein auf eingeschriebenen Android-Testgeräten. '
      + 'Eine **nachweislich gefälschte App lässt das NICHT durch** — der Türsteher fragt diesen '
      + 'Ersatzweg gar nicht ab.',
  },
];
const NOT_DECIDING_EFFECT = 'Nichts wird blockiert. Die Standortbewertung weist heute niemanden ab — '
  + 'der Fall bleibt offen stehen und verfällt mit der Aufbewahrungsfrist. Nicht entscheiden ist also '
  + 'zulässig; es bedeutet nur, dass die Frage unbeantwortet bleibt.';

// Fett-Auszeichnung in den Wirkungstexten, ohne HTML aus Daten zu bauen.
const withEmphasis = (text) => text.split('**').map((part, index) => (
  index % 2 ? <strong key={index}>{part}</strong> : <React.Fragment key={index}>{part}</React.Fragment>
));

// Audit-Aktionen: die zehn `locationTrustAudit`-Aufrufe in `routes/admin.js`.
const AUDIT_ACTION_TEXT = {
  list: 'Liste geöffnet',
  view: 'Vorgang geöffnet',
  exact_location_reveal: 'Genaue Koordinaten aufgedeckt',
  live_location_view: 'Live-Standort angesehen',
  decision: 'Entscheidung getroffen',
  appeal: 'Einspruch eröffnet',
  config_view: 'Einstellungen angesehen',
  config_change: 'Einstellungen geändert',
  retention_approval_created: 'Retention-Freigabe erfasst',
  retention_approval_revoked: 'Retention-Freigabe zurückgezogen',
};

const actorText = (actorType) => (actorType === 'driver' ? 'Fahrer'
  : actorType === 'customer' ? 'Kunde' : 'Beteiligter');

// Die Liste war nie eine Warteschlange: `/cases` liefert ALLE Evidenzzeilen, auch die
// unauffälligen. Getrennt wird hier im Browser aus bereits geliefertem `decision` und
// `review_decision` — keine neue Abfrage, kein Filter am Server.
const NEEDS_DECISION = (item) => item.decision === 'review' && !item.review_decision;
const caseGroups = (cases) => {
  const open = cases.filter(NEEDS_DECISION);
  const decided = cases.filter((item) => !!item.review_decision);
  const routine = cases.filter((item) => item.decision !== 'review' && !item.review_decision);
  return [
    { key: 'open', title: 'Wartet auf Ihre Entscheidung', items: open,
      empty: 'Nichts zu prüfen. Kein Fall wartet gerade auf eine Entscheidung von Ihnen.' },
    { key: 'decided', title: 'Von Ihnen bereits entschieden', items: decided,
      empty: 'Noch nichts entschieden.' },
    { key: 'routine', title: 'Unauffällig — nur mitgeschrieben', items: routine,
      empty: 'Keine unauffälligen Vorgänge in diesem Zeitraum.' },
  ];
};

function LockNote({ children, testId }) {
  return (
    <small className="lt-lock-note" data-testid={testId}>
      <LockKeyhole size={11} /> <span>{children}</span>
    </small>
  );
}

function ConfigPath({ steps }) {
  return (
    <section className="lt-path" data-testid="config-path">
      <div className="lt-section-heading">
        <div><span>Reihenfolge</span><h3>Der Weg zum scharfen Schutz</h3></div>
        <ListChecks size={16} />
      </div>
      <p className="lt-path-intro">
        Die Schalter sind nicht kaputt — sie sind verriegelt und gehen erst der Reihe nach auf.
        Was oben nicht erledigt ist, hält alles darunter grau.
      </p>
      <ol className="lt-path-steps">
        {steps.map((step, index) => (
          <li key={step.key} className={`lt-path-step is-${step.done ? 'done' : 'open'}`}
            data-testid={`path-step-${step.key}`}>
            <span className="lt-path-index">{step.done ? <CheckCircle2 size={14} /> : index + 1}</span>
            <div>
              <strong>{step.title}</strong>
              <span className="lt-path-state">{step.state}{step.perPlatform ? ` — ${step.perPlatform}` : ''}</span>
              <small>{step.detail}</small>
              <small className="lt-path-meta">{step.who} Schaltet frei: {step.unlocks}.</small>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function DecisionPill({ value }) {
  return (
    <span className="lt-decision" style={{ '--decision-color': COLORS[value] || '#4b5563' }}>
      <i /> {value || 'offen'}
    </span>
  );
}

function ProviderCard({ platform, provider }) {
  const name = platform === 'android' ? 'Android' : 'iOS';
  const ready = provider?.ready === true;
  return (
    <article className={`lt-provider ${ready ? 'is-ready' : ''}`} data-testid={`provider-${platform}`}>
      <header>
        <span className="lt-provider-icon"><Smartphone size={17} /></span>
        <div><span>Attestation</span><strong>{name}</strong></div>
        <span className={`lt-provider-state ${ready ? 'is-ready' : ''}`}>{ready ? 'bereit' : 'nicht bereit'}</span>
      </header>
      <p>
        {name}: {provider?.credentials_configured ? 'Konfiguration vorhanden' : 'Konfiguration fehlt'}
        {' · '}{provider?.configuration_valid ? 'Syntax gültig' : 'Syntax ungültig'}
        {' · '}{provider?.physical_e2e_confirmed ? 'physischer E2E bestätigt' : 'physischer E2E offen'}
        {' · '}{provider?.ready ? 'produktionsbereit' : 'nicht produktionsbereit'}
        {' · '}letzter gemeinsamer Erfolg {fmt(provider?.last_success_at_ms)}
        {provider?.error_category ? ` · Fehlerkategorie ${provider.error_category}` : ''}
      </p>
      {!ready && provider?.reason && (
        <p className="lt-provider-hint" data-testid={`provider-${platform}-hint`}>
          {PROVIDER_REASON_HINTS[platform]?.[provider.reason] || `Grund: ${provider.reason}`}
        </p>
      )}
      {provider?.apps && (
        <ul>
          {['driver', 'customer'].map((role) => (
            <li key={role}>
              <strong>{role === 'driver' ? 'Fahrer-App' : 'Kunden-App'}:</strong> {provider.apps[role]?.app_identity || 'Identität fehlt'}
              {' · '}{provider.apps[role]?.configuration_confirmed ? 'aktuelle Konfiguration bestätigt' : 'aktuelle Konfiguration offen'}
              {' · '}letzter Erfolg {fmt(provider.apps[role]?.last_success_at_ms)}
              {provider.apps[role]?.error_category ? ` · Fehlerkategorie ${provider.apps[role].error_category}` : ''}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

export default function LocationTrust() {
  const { can, user } = useAuth();
  const canManage = can && can('location_trust.manage');
  const canAppeal = can && can('location_trust.appeal');
  const canConfig = can && can('location_trust.config');
  const canOwner = can && can('settings.manage');
  const canView = can && (can('location_trust.view') || canManage || canAppeal);
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [exact, setExact] = useState(null);
  const [error, setError] = useState('');
  const [reasonCode, setReasonCode] = useState('manual_review');
  const [operatorNote, setOperatorNote] = useState('');
  const [config, setConfig] = useState(null);
  const [savedConfig, setSavedConfig] = useState(null);
  const [configRevision, setConfigRevision] = useState(0);
  const [providers, setProviders] = useState(null);
  const [retentionApproval, setRetentionApproval] = useState(null);
  const [retentionContractHash, setRetentionContractHash] = useState('');
  const [testEnforcement, setTestEnforcement] = useState(null);
  const [canEnableTestEnforcement, setCanEnableTestEnforcement] = useState(false);
  const [ownerConfirm, setOwnerConfirm] = useState(false);
  const [ownerReason, setOwnerReason] = useState('');
  const [approvalReference, setApprovalReference] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [approvalDate, setApprovalDate] = useState('');

  const load = useCallback(() => api.get('/admin/location-trust/cases').then((response) => {
    setCases(response.data.cases || []);
    setError('');
  }).catch(() => setError('Evidenz konnte nicht geladen werden.')), []);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  useEffect(() => {
    if (!canConfig) return undefined;
    api.get('/admin/location-trust/config').then((response) => {
      setConfig(response.data.config);
      setSavedConfig(response.data.config);
      setConfigRevision(response.data.revision);
      setProviders(response.data.providers || null);
      setRetentionApproval(response.data.retention_approval || null);
      setRetentionContractHash(response.data.retention_contract_hash || '');
      setTestEnforcement(response.data.test_enforcement || null);
      setCanEnableTestEnforcement(response.data.can_enable_test_enforcement === true);
    }).catch(() => setError('Standort-Konfiguration konnte nicht geladen werden.'));
    return undefined;
  }, [canConfig]);

  useEffect(() => {
    if (!canManage || !canView) return undefined;
    const socket = io(apiOrigin(), {
      auth: { token: localStorage.getItem('admin_token') },
      extraHeaders: { 'ngrok-skip-browser-warning': 'true' },
    });
    socket.on('connect', () => socket.emit('join_location_trust'));
    socket.on('location_trust_changed', load);
    return () => socket.disconnect();
  }, [canManage, canView, load]);

  const open = async (id) => {
    setExact(null);
    try {
      const response = await api.get(`/admin/location-trust/cases/${id}`);
      setSelected(response.data);
    } catch {
      setError('Fall konnte nicht geladen werden.');
    }
  };

  const reveal = async () => {
    if (!selected || !canManage) return;
    const response = await api.get(`/admin/location-trust/cases/${selected.evidence.id}/location`);
    setExact(response.data.events || []);
  };

  const decide = async (decision) => {
    const revision = selected.decisions?.length ? selected.decisions[selected.decisions.length - 1].revision : 0;
    await api.post(`/admin/location-trust/cases/${selected.evidence.id}/decision`, {
      decision,
      reason_code: reasonCode,
      note: operatorNote,
      expected_revision: revision,
    });
    await open(selected.evidence.id);
    await load();
  };

  const appeal = async () => {
    const revision = selected.decisions?.length ? selected.decisions[selected.decisions.length - 1].revision : 0;
    await api.post(`/admin/location-trust/cases/${selected.evidence.id}/appeal`, {
      note: operatorNote,
      expected_revision: revision,
    });
    await open(selected.evidence.id);
    await load();
  };

  const saveConfig = async () => {
    // Providerbereitschaft gilt für Moduswechsel; unveränderte Modi dürfen eine
    // unabhängige Radiuskorrektur nicht blockieren. Die API prüft den Übergang erneut.
    const blockedPlatform = ['android', 'ios'].find((platform) =>
      config[`${platform}_attestation_mode`] !== 'off'
      && config[`${platform}_attestation_mode`] !== savedConfig?.[`${platform}_attestation_mode`]
      && providers?.[platform]?.activation_ready !== true);
    if (blockedPlatform) {
      setError('Attestation kann erst nach sicher hinterlegter Provider-Konfiguration aktiviert werden. '
        + providerBlockText(blockedPlatform, providers?.[blockedPlatform]));
      return;
    }
    try {
      const response = await api.patch('/admin/location-trust/config', {
        expected_revision: configRevision,
        config,
        confirm: ownerConfirm,
        reason: ownerReason,
      });
      setConfig(response.data.config);
      setSavedConfig(response.data.config);
      setConfigRevision(response.data.revision);
      setError('');
      setOwnerConfirm(false);
      setOwnerReason('');
    } catch (requestError) {
      const data = requestError?.response?.data;
      setError(data?.error === 'attestation_provider_not_configured'
        ? `Attestation-Provider ist nicht konfiguriert; Modus bleibt off. ${
          providerBlockText(data.platform === 'ios' ? 'ios' : 'android',
            providers?.[data.platform === 'ios' ? 'ios' : 'android'])}`
        : data?.error === 'attestation_provider_not_ready'
          ? 'Türsteher (enforce) kann erst eingeschaltet werden, wenn beide echten Apps die Prüfung mit der aktuellen Konfiguration innerhalb der letzten 24 Stunden bestanden haben (physischer E2E).'
          : data?.error === 'attestation_enforce_confirmation_required'
            ? 'Türsteher (enforce) einschalten braucht die ausdrückliche Owner-Bestätigung samt Begründung (Häkchen und Text unten im Formular).'
            : 'Standort-Konfiguration konnte nicht gespeichert werden.');
    }
  };

  const createRetentionApproval = async () => {
    if (!canOwner || !user?.id) return;
    try {
      const response = await api.post('/admin/location-trust/retention-approvals', {
        confirm: ownerConfirm,
        approval_reference: approvalReference,
        reason: approvalReason,
        approval_date: approvalDate,
        responsible_admin_id: user.id,
        contract_hash: retentionContractHash,
      });
      setRetentionApproval(response.data.approval);
      setOwnerConfirm(false);
      setError('');
    } catch {
      setError('Retention-Freigabe konnte nicht auditiert gespeichert werden.');
    }
  };

  const selectedDecision = selected?.evidence?.review_decision || selected?.evidence?.decision;

  return (
    <div className="lt-page">
      <PageHeader
        title="Standort-Evidenz"
        description="Hier wird geprüft, ob eine Fahrt-Aktion wirklich von dort und von der App kommt, die sie behauptet — und hier wird eingestellt, wie streng das läuft."
        actions={
        <div className="lt-heading-badges">
          <span><Radio size={14} /> {cases.length} Evidenzfälle</span>
          <span><LockKeyhole size={14} /> Exact-Reveal auditiert</span>
        </div>
      }
      />

      <section className="lt-safety-notice">
        <span className="lt-safety-icon"><ShieldCheck size={19} /></span>
        <div>
          <strong>Read-first Sicherheitsmodus</strong>
          <p>Keine automatische Sperre aus der Standortbewertung und keine Preisänderung. Einzige Ausnahme: der Owner-geschaltete Attestierungs-Türsteher (enforce) weist nachweislich durchgefallene Apps ab — „nicht prüfbar" kommt immer durch. Exakte Standortpunkte bleiben verborgen, bis ein berechtigter Admin sie ausdrücklich und auditiert öffnet.</p>
        </div>
        <span className="lt-safety-mode">Observe / Shadow{(config?.android_attestation_mode === 'enforce' || config?.ios_attestation_mode === 'enforce') ? ' / Enforce' : ''}</span>
      </section>

      {error && <div className="lt-error" role="alert"><AlertTriangle size={15} /> {error}</div>}

      {canView && (
        <section className={`lt-workspace ${selected ? 'has-selection' : ''}`}>
          <aside className="lt-case-panel">
            <header className="lt-panel-heading">
              <div>
                <span>Was zu tun ist</span>
                <h2>Vorgänge</h2>
              </div>
              <strong data-testid="open-count">{cases.filter(NEEDS_DECISION).length}</strong>
            </header>
            <p className="lt-case-intro">
              Die große Zahl sind die Vorgänge, die auf <em>Ihre</em> Entscheidung warten — nicht
              alle Zeilen. Der Rest steht nur zur Nachvollziehbarkeit da.
            </p>
            <div className="lt-case-list">
              {!cases.length && (
                <div className="lt-empty" data-testid="cases-empty">
                  <CheckCircle2 size={22} />
                  <strong>Nichts zu prüfen</strong>
                  <small>
                    Es liegt noch kein einziger Vorgang vor. Das ist der normale Zustand, solange die
                    Standortprüfung nicht läuft — dann entsteht auch nichts, was geprüft werden könnte.
                  </small>
                </div>
              )}
              {!!cases.length && caseGroups(cases).map((group) => (
                <section className="lt-case-group" key={group.key} data-testid={`case-group-${group.key}`}>
                  <header>
                    <strong>{group.title}</strong>
                    <span>{group.items.length}</span>
                  </header>
                  {!group.items.length && (
                    <p className="lt-case-group-empty" data-testid={`case-group-${group.key}-empty`}>
                      {group.empty}
                    </p>
                  )}
                  {group.items.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      data-testid={`case-row-${item.id}`}
                      className={`lt-case-row ${selected?.evidence?.id === item.id ? 'is-selected' : ''}`}
                      onClick={() => open(item.id)}
                    >
                      <span className="lt-case-main">
                        {/* Was ist passiert · wer · wann — in dieser Reihenfolge, weil der
                            Owner zuerst wissen will, WAS er vor sich hat. */}
                        <strong>{actionText(item.action)}</strong>
                        <small>
                          {actorText(item.actor_type)}
                          {item.actor_name ? ` ${item.actor_name}` : ''} · {fmt(item.created_at_ms)}
                        </small>
                        {/* Warum liegt es vor — der erste Grund im Klartext, Rest gezählt. */}
                        <small className="lt-case-why">
                          {item.decision === 'review'
                            ? `${reasonText((item.reasons || [])[0] || 'unbekannt')}${
                              (item.reasons || []).length > 1 ? ` (+${item.reasons.length - 1} weitere)` : ''}`
                            : caseVerdict(item.decision).text}
                        </small>
                      </span>
                      <span className="lt-case-side">
                        <DecisionPill value={item.review_decision || item.decision} />
                        {item.enforce === 1 || item.enforce === true
                          ? <small className="lt-case-enforced">abgewiesen</small> : null}
                        <ChevronRight size={14} />
                      </span>
                    </button>
                  ))}
                </section>
              ))}
            </div>
          </aside>

          {selected ? (
            <article className="lt-detail-panel">
              <header className="lt-detail-heading">
                <div>
                  <span>Vorgang #{String(selected.evidence.id).slice(0, 8)}</span>
                  <h2>{actionText(selected.evidence.action)}</h2>
                </div>
                <DecisionPill value={selectedDecision} />
              </header>

              <div className="lt-detail-body">
                {/* Die sechs Fragen der Karte, in der Reihenfolge, in der ein Mensch sie stellt.
                    Alle Angaben liefert der Server längst mit — sie wurden nur nie gezeigt. */}
                <section className="lt-answers" data-testid="case-answers">
                  <div>
                    <span>Was ist passiert?</span>
                    <strong>{actionText(selected.evidence.action)}</strong>
                    <small>{selected.evidence.ride_id ? `Fahrt ${selected.evidence.ride_id}` : 'ohne Fahrtbezug'}</small>
                  </div>
                  <div>
                    <span>Wer war beteiligt?</span>
                    <strong>
                      {actorText(selected.evidence.actor_type)}
                      {selected.evidence.actor_name ? ` ${selected.evidence.actor_name}` : ''}
                    </strong>
                    <small>Die Person, die die Aktion ausgelöst hat.</small>
                  </div>
                  <div>
                    <span>Wann?</span>
                    <strong>{fmt(selected.evidence.created_at_ms)}</strong>
                    <small>
                      {selected.evidence.last_server_received_at_ms
                        ? `letzte Standortmeldung ${fmt(selected.evidence.last_server_received_at_ms)}`
                        : 'keine Standortmeldung dazu'}
                    </small>
                  </div>
                  <div className={`is-${caseVerdict(selected.evidence.decision).tone}`}>
                    <span>Warum liegt es mir vor?</span>
                    <strong>{caseVerdict(selected.evidence.decision).label}</strong>
                    <small>{caseVerdict(selected.evidence.decision).text}</small>
                  </div>
                </section>

                {(selected.evidence.reasons || []).length > 0 && (
                  <section className="lt-section" data-testid="case-reasons">
                    <div className="lt-section-heading">
                      <div><span>Im Klartext</span><h3>Was das System beobachtet hat</h3></div>
                      <FileSearch size={16} />
                    </div>
                    <ul className="lt-reason-list">
                      {(selected.evidence.reasons || []).map((reason) => (
                        <li key={reason}>
                          {reasonText(reason)}
                          <em>{reason}</em>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Genau die Frage „war das die gefälschte App?" — sie kam immer schon im
                    Datenpaket mit (`attestation_state`/`attestation_reason`) und wurde nie gezeigt. */}
                <section className="lt-section" data-testid="case-attestation">
                  <div className="lt-section-heading">
                    <div><span>Echtheit der App</span><h3>Kam das aus der echten VOOGO-App?</h3></div>
                    <Fingerprint size={16} />
                  </div>
                  {selected.evidence.attestation_state ? (
                    <>
                      <p className={`lt-verdict is-${(ATTESTATION_STATE_TEXT[selected.evidence.attestation_state]
                        || { tone: 'neutral' }).tone}`}>
                        {(ATTESTATION_STATE_TEXT[selected.evidence.attestation_state]
                          || { text: `Zustand „${selected.evidence.attestation_state}" — nicht übersetzt.` }).text}
                      </p>
                      {selected.evidence.attestation_reason && (
                        <p className="lt-verdict-detail">
                          {ATTESTATION_REASON_TEXT[selected.evidence.attestation_reason]
                            || `Grund des Anbieters: „${selected.evidence.attestation_reason}".`}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="lt-verdict is-neutral">
                      Zu diesem Vorgang wurde keine Echtheitsprüfung mitgeschrieben — sie war für
                      diese Aktion nicht eingeschaltet.
                    </p>
                  )}
                  <p className="lt-verdict-detail">
                    {selected.evidence.enforce === 1 || selected.evidence.enforce === true
                      ? 'Diese Aktion wurde tatsächlich abgewiesen — sie ist nicht durchgegangen.'
                      : 'Diese Aktion ist durchgegangen. Abgewiesen wurde nichts.'}
                  </p>
                  {selected.evidence.override_kind && (
                    <p className="lt-verdict-detail" data-testid="case-override">
                      Es griff ein Ersatznachweis
                      {selected.evidence.override_kind === 'support' ? ' aus einer Support-Freigabe'
                        : selected.evidence.override_kind === 'pin' ? ': die vom Kunden genannte PIN war serverseitig bestätigt'
                          : selected.evidence.override_kind === 'customer_confirmation' ? ': der Kunde hat die Aktion selbst bestätigt'
                            : ` der Art „${selected.evidence.override_kind}"`}.
                      {selected.evidence.override_reason ? ` (${selected.evidence.override_reason})` : ''}
                    </p>
                  )}
                </section>

                <section className="lt-section">
                  <div className="lt-section-heading">
                    <div><span>Die Standortmeldungen dahinter</span><h3>Zeitlinie</h3></div>
                    <Clock3 size={16} />
                  </div>
                  <p className="lt-section-intro">
                    Diese Punkte hat das System für sein Urteil herangezogen. „±" ist die
                    Ungenauigkeit in Metern — je größer die Zahl, desto weniger sagt der Punkt aus.
                  </p>
                  <div className="lt-timeline">
                    {(selected.events || []).map((event) => (
                      <div className="lt-timeline-event" key={event.id}>
                        <span className="lt-timeline-sequence">{event.sequence}</span>
                        <div>
                          <strong>Eingegangen {fmt(event.server_received_at_ms)}</strong>
                          <small>
                            auf ±{event.horizontal_accuracy_m} m genau ·{' '}
                            {event.quality === 'good' ? 'gute Qualität'
                              : event.quality === 'degraded' ? 'schwache Qualität'
                                : event.quality === 'poor' ? 'schlechte Qualität' : `Qualität ${event.quality}`}
                          </small>
                          <p>
                            {(event.reasons || []).length
                              ? (event.reasons || []).map(reasonText).join(' ')
                              : 'An diesem Punkt war nichts auffällig.'}
                          </p>
                        </div>
                      </div>
                    ))}
                    {!(selected.events || []).length && (
                      <p className="lt-muted">Zu diesem Vorgang wurden keine Standortpunkte herangezogen.</p>
                    )}
                  </div>
                </section>

                <section className="lt-section lt-exact-section">
                  <div className="lt-section-heading">
                    <div><span>Geschützter Zugriff</span><h3>Wo genau war das Gerät?</h3></div>
                    <LockKeyhole size={16} />
                  </div>
                  <p>
                    Die genauen Koordinaten bleiben absichtlich verborgen — auch für Sie. Für die
                    Entscheidung braucht es sie meist nicht: Ob jemand nah genug dran war, steht
                    bereits oben im Klartext. Wenn Sie sie trotzdem aufdecken, wird <strong>Ihr Name
                    mit Zeitpunkt festgehalten</strong> und steht unten im Protokoll. Das ist kein
                    Verdacht gegen Sie, sondern der Datenschutz der beteiligten Person.
                  </p>
                  {canManage && (
                    <button type="button" className="lt-button is-secondary" onClick={reveal}>
                      <Eye size={15} /> Exakte Position auditiert anzeigen
                    </button>
                  )}
                  {exact && <pre data-testid="exact-location" className="lt-exact-data">{JSON.stringify(exact, null, 2)}</pre>}
                </section>

                <section className="lt-section">
                  <div className="lt-section-heading">
                    <div><span>Ihre Entscheidung</span><h3>Was passiert, wenn Sie klicken</h3></div>
                    <UserCheck size={16} />
                  </div>

                  {/* Der Kern der Karte: vor dem Klick steht da, was der Klick bewirkt.
                      Jeder Satz ist am Code gemessen (siehe DECISION_EFFECT). */}
                  {canManage && (
                    <ul className="lt-effect-list" data-testid="decision-effects">
                      {DECISION_EFFECT.map((entry) => (
                        <li key={entry.key} data-testid={`effect-${entry.key}`}>
                          <strong>{entry.label}</strong>
                          <span>{withEmphasis(entry.effect)}</span>
                        </li>
                      ))}
                      <li className="is-neutral" data-testid="effect-none">
                        <strong>Gar nicht entscheiden</strong>
                        <span>{NOT_DECIDING_EFFECT}</span>
                      </li>
                    </ul>
                  )}

                  {(canManage || canAppeal) && (
                    <div className="lt-decision-form">
                      <label>
                        <span>Womit begründen Sie das?</span>
                        <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)}>
                          <option value="manual_review">Von Hand geprüft</option>
                          <option value="support_verified">Support hat es überprüft</option>
                          <option value="gps_degraded">Schlechtes GPS war die Ursache</option>
                          <option value="customer_confirmed">Der Kunde hat es bestätigt</option>
                          <option value="false_positive">Fehlalarm</option>
                        </select>
                      </label>
                      <label>
                        <span>Ihre Notiz (Pflicht)</span>
                        <textarea value={operatorNote} onChange={(event) => setOperatorNote(event.target.value)} maxLength={1000} />
                      </label>
                    </div>
                  )}
                  {canManage && !operatorNote.trim() && (
                    <LockNote testId="decision-note-required">
                      Die Knöpfe bleiben gesperrt, bis eine Notiz dasteht. Sie wird unveränderlich
                      mitgeschrieben — wer später fragt, warum so entschieden wurde, liest genau diesen Satz.
                    </LockNote>
                  )}
                  {canManage && (
                    <div className="lt-decision-actions">
                      <button type="button" className="lt-button is-danger" disabled={!operatorNote.trim()} onClick={() => decide('confirmed')}>Auffälligkeit bestätigen</button>
                      <button type="button" className="lt-button is-secondary" disabled={!operatorNote.trim()} onClick={() => decide('dismissed')}>Verwerfen</button>
                      <button type="button" className="lt-button is-secondary" disabled={!operatorNote.trim()} onClick={() => decide('support_override')}>Support-Override</button>
                    </div>
                  )}
                  {canAppeal && (
                    <>
                      <button type="button" className="lt-button is-appeal" disabled={!operatorNote.trim()} onClick={appeal}>Einspruch eröffnen</button>
                      <p className="lt-section-intro">
                        Ein Einspruch entscheidet nichts — er hält fest, dass der Betroffene mit der
                        Bewertung nicht einverstanden ist.
                      </p>
                    </>
                  )}
                  {(selected.decisions || []).length > 0 && (
                    <div className="lt-decision-history" data-testid="decision-history">
                      <strong className="lt-history-title">Bisher entschieden</strong>
                      {(selected.decisions || []).map((decision) => (
                        <p key={decision.revision}>
                          <strong>
                            {DECISION_EFFECT.find((entry) => entry.key === decision.decision)?.label
                              || (decision.decision === 'degraded_override' ? 'Freigabe bei schlechtem GPS'
                                : decision.decision === 'appeal' ? 'Einspruch eröffnet' : decision.decision)}
                          </strong>
                          <span>
                            {decision.decided_by_name ? `${decision.decided_by_name} · ` : ''}
                            {fmt(decision.decided_at_ms)}
                          </span>
                          {decision.note ? <em>„{decision.note}"</em> : null}
                        </p>
                      ))}
                    </div>
                  )}
                </section>

                <section className="lt-section">
                  <div className="lt-section-heading">
                    <div><span>Unveränderlich</span><h3>Wer hat diesen Vorgang angesehen</h3></div>
                    <Database size={16} />
                  </div>
                  <div className="lt-audit-list">
                    {(selected.audit || []).map((entry, index) => (
                      <p key={`${entry.created_at_ms}-${entry.action}-${index}`}>
                        <span>{fmt(entry.created_at_ms)}</span>
                        <strong>{AUDIT_ACTION_TEXT[entry.action] || entry.action}</strong>
                        <span>{entry.actor_name || entry.actor_user_id}</span>
                        {Object.keys(entry.detail || {}).length ? <small>{JSON.stringify(entry.detail)}</small> : null}
                      </p>
                    ))}
                    {!(selected.audit || []).length && <p className="lt-muted">Diesen Vorgang hat bisher niemand geöffnet.</p>}
                  </div>
                </section>
              </div>
            </article>
          ) : (
            <div className="lt-detail-placeholder">
              <FileSearch size={25} />
              <strong>Links einen Vorgang anklicken</strong>
              <small>
                Dann steht hier, was passiert ist, wer beteiligt war, warum es Ihnen vorliegt und
                was Ihre Entscheidung bewirkt. Genaue Koordinaten bleiben dabei verborgen, bis Sie
                sie ausdrücklich aufdecken.
              </small>
            </div>
          )}
        </section>
      )}

      {canConfig && config && (
        <section className="lt-config-panel">
          <header className="lt-config-heading">
            <div>
              <span>Owner- und Provider-gated</span>
              <h2>Sicherheitsmodus und Aufbewahrung</h2>
              <p>Änderungen bleiben revisionsgebunden und werden ausschließlich über den auditierten Konfigurationsendpunkt gespeichert.</p>
            </div>
            <span className="lt-revision">Revision {configRevision}</span>
          </header>

          <div className="lt-config-body">
            <section className="lt-config-section">
              <div className="lt-section-heading"><div><span>Was hier eingestellt wird</span><h3>Betriebsarten</h3></div><Activity size={16} /></div>

              <ConfigPath steps={configPath(config, retentionApproval, providers)} />

              <dl className="lt-mode-legend" data-testid="mode-legend">
                {MODE_GLOSSARY.map(({ mode, text }) => (
                  <div key={mode}><dt>{mode}</dt><dd>{text}</dd></div>
                ))}
              </dl>

              <div className="lt-mode-grid">
                {MODE_FIELDS.map(({ field, platform, label, effect }) => {
                  const providerUnavailable = platform && providers?.[platform]?.activation_ready !== true;
                  const modeUnavailable = !config.retention_enabled || providerUnavailable;
                  // ATTESTIERUNG-TUERSTEHER-001: enforce (abweisen) gibt es nur für die
                  // Attestierungsmodi und erst mit frisch bestätigtem physischem E2E (`ready`).
                  // Ist enforce bereits aktiv, bleibt die Option wählbar — Ausschalten ist nie gesperrt.
                  const enforceUnavailable = modeUnavailable || providers?.[platform]?.ready !== true;
                  // ATTESTIERUNG-VERSTAENDLICH-001: nur Text. `modeUnavailable` und
                  // `enforceUnavailable` oben entscheiden weiterhin allein, was gesperrt ist.
                  const lockText = modeUnavailable
                    ? modeLockText(platform, config, retentionApproval, providers) : null;
                  const enforceLocked = platform && !modeUnavailable
                    && enforceUnavailable && config[field] !== 'enforce';
                  return (
                    <label className={`lt-field ${modeUnavailable ? 'is-locked' : ''}`} key={field}
                      data-testid={`mode-${field}`}>
                      <span>{label}</span>
                      <select value={config[field]} onChange={(event) => setConfig({ ...config, [field]: event.target.value })}>
                        <option value="off">off</option>
                        <option value="observe" disabled={modeUnavailable}>observe</option>
                        <option value="shadow" disabled={modeUnavailable}>shadow</option>
                        {platform && (
                          <option value="enforce" disabled={enforceUnavailable && config[field] !== 'enforce'}>
                            enforce (abweisen)
                          </option>
                        )}
                      </select>
                      <small>{effect}</small>
                      {lockText && <LockNote testId={`mode-${field}-lock`}>{lockText}</LockNote>}
                      {enforceLocked && (
                        <LockNote testId={`mode-${field}-enforce-lock`}>{enforceLockText(platform)}</LockNote>
                      )}
                      <small className="lt-field-origin">technisch: {field}</small>
                    </label>
                  );
                })}
              </div>

              <div className="lt-switch-grid">
                <label className="lt-switch">
                  <input type="checkbox" checked={config.legacy_v1_enabled} onChange={(event) => setConfig({ ...config, legacy_v1_enabled: event.target.checked })} />
                  <span>
                    <strong>Ältere App-Versionen weiter zulassen</strong>
                    <small>
                      Ist das an, dürfen alte Apps ihre Position noch ohne den neuen, signierten
                      Umschlag melden; solche Punkte gelten als ungeprüft und werden nicht bewertet.
                      Aus heißt: solche Meldungen werden abgewiesen.
                    </small>
                  </span>
                </label>
                <label className={`lt-switch ${!config.retention_enabled && !retentionApproval ? 'is-locked' : ''}`}>
                  <input type="checkbox" data-testid="retention-switch"
                    checked={config.retention_enabled} disabled={!config.retention_enabled && !retentionApproval}
                    onChange={(event) => setConfig({ ...config, retention_enabled: event.target.checked })} />
                  <span>
                    <strong>Aufbewahrung (Retention)</strong>
                    <small>
                      Der Hauptschalter. Ist er aus, sind alle vier Betriebsarten oben gesperrt —
                      ohne Aufbewahrung darf nichts festgehalten und deshalb nichts geprüft werden.
                    </small>
                    {!config.retention_enabled && !retentionApproval && (
                      <LockNote testId="retention-switch-lock">
                        Gesperrt, weil noch keine Freigabe vorliegt. Nächster Schritt 1: unten
                        „Retention-Freigabe auditiert erfassen" mit Aktenzeichen, Freigabedatum,
                        Begründung und Häkchen — dann wird dieser Schalter bedienbar.
                      </LockNote>
                    )}
                  </span>
                </label>
                <label className={`lt-switch ${!config.test_enforcement_enabled
                  && (!canEnableTestEnforcement || testEnforcement?.activation_ready !== true) ? 'is-locked' : ''}`}>
                  <input type="checkbox" data-testid="test-enforcement-switch"
                    checked={config.test_enforcement_enabled === true}
                    disabled={!config.test_enforcement_enabled
                      && (!canEnableTestEnforcement || testEnforcement?.activation_ready !== true)}
                    onChange={(event) => setConfig({ ...config, test_enforcement_enabled: event.target.checked })} />
                  <span>
                    <strong>Abweisung auf zugelassenen Android-Testgeräten</strong>
                    <small>
                      Ist das an, werden ausschließlich die ausdrücklich eingetragenen
                      Android-Testgeräte wirklich abgewiesen, wenn ihre Prüfung durchfällt.
                      Alle übrigen Geräte bleiben unberührt. Ausschalten ist nie gesperrt.
                    </small>
                    {!config.test_enforcement_enabled
                      && (!canEnableTestEnforcement || testEnforcement?.activation_ready !== true) && (
                      <LockNote testId="test-enforcement-lock">
                        {!canEnableTestEnforcement
                          ? 'Gesperrt: Dieser Zugang darf den Schalter nicht einschalten — das kann nur ein '
                            + 'besonders geschützter Owner-Zugang. '
                          : ''}
                        {(testEnforcement?.blockers || []).length
                          ? `Es fehlt noch: ${(testEnforcement.blockers || [])
                            .map((blocker) => TEST_ENFORCEMENT_BLOCKER_TEXT[blocker] || blocker).join(' ')}`
                          : canEnableTestEnforcement
                            ? 'Die Aktivierungsgates sind noch nicht erfüllt.' : ''}
                      </LockNote>
                    )}
                  </span>
                </label>
              </div>
            </section>

            {/* Aktionsradien (1.251.4): Die Werte waren serverseitig laengst konfigurierbar, aber
                die Oberflaeche zeigte sie nicht — es gab also keinen Weg, sie zu setzen. Sie
                entscheiden, wie weit der Fahrer vom Abhol- bzw. Zielort entfernt sein darf, um
                Ankunft und Abschluss auszuloesen. Grenzen und Standardwerte kommen aus derselben
                Quelle wie die Policy (LOCATION_CONFIG_FIELDS in routes/admin.js). */}
            <section className="lt-config-section">
              <div className="lt-section-heading"><div><span>Fahrt-Aktionen</span><h3>Aktionsradien</h3></div><Activity size={16} /></div>
              <div className="lt-mode-grid">
                {[
                  { field: 'action_radius_m', label: 'Aktionsradius (m)',
                    hint: 'Ankunft und Abschluss. Hoeher = der Fahrer kann weiter entfernt abschliessen.',
                    min: 50, max: 10000 },
                  { field: 'auto_arrive_radius_m', label: 'Automatische Ankunft (m)',
                    hint: 'Ab dieser Naehe meldet die App die Ankunft von selbst.',
                    min: 25, max: 1000 },
                ].map(({ field, label, hint, min, max }) => (
                  <label className="lt-field" key={field}>
                    <span>{label}</span>
                    <input
                      type="number" min={min} max={max} step={10}
                      // Ist die Grenze abgeschaltet, spielt der Zahlenwert keine Rolle mehr —
                      // ein bedienbares Feld ohne Wirkung wäre irreführend.
                      disabled={field === 'action_radius_m' && config.action_radius_unlimited === true}
                      value={Number.isFinite(config[field]) ? config[field] : ''}
                      onChange={(event) => {
                        // Leeres Feld nicht als 0 oder NaN in den Zustand schreiben — sonst weist
                        // der Server die GANZE Konfiguration ab (Regression 1.251.2).
                        const next = Number.parseInt(event.target.value, 10);
                        if (Number.isSafeInteger(next)) setConfig({ ...config, [field]: next });
                      }} />
                    <small>{hint} Erlaubt {min}–{max}.</small>
                  </label>
                ))}
              </div>

              <div className="lt-switch-grid">
                <label className="lt-switch">
                  <input type="checkbox" data-testid="action-radius-unlimited"
                    checked={config.action_radius_unlimited === true}
                    onChange={(event) => setConfig({ ...config, action_radius_unlimited: event.target.checked })} />
                  <span>
                    <strong>Keine Abstandsgrenze für Start und Abschluss</strong>
                    <small>
                      Der Fahrer kann Ankunft und Abschluss aus jeder Entfernung auslösen; der
                      Aktionsradius wird dann ignoriert. Gilt NICHT für die automatische Ankunft —
                      die braucht Nähe, sonst meldet sie kilometerweit vorher „angekommen".
                    </small>
                  </span>
                </label>
              </div>
            </section>

            <section className="lt-retention-section">
              <div className="lt-section-heading"><div><span>Aufbewahrungsvertrag</span><h3>Retention und Freigaben</h3></div><ShieldAlert size={16} /></div>
              <div className="lt-retention-metrics">
                <span><strong>{config.raw_retention_hours} h</strong>Rohpunkte</span>
                <span><strong>{config.evidence_retention_days} d</strong>Evidenz</span>
                <span><strong>{config.audit_retention_days} d</strong>Audit</span>
              </div>
              <p>
                So lange werden die Daten aufbewahrt: {config.raw_retention_hours} h die rohen
                Positionspunkte, {config.evidence_retention_days} d die Evidenzfälle,
                {' '}{config.audit_retention_days} d das Audit-Protokoll. Einschalten darf das nur der
                Owner mit einer schriftlichen Freigabe (Legal/DPO) — das ist Schritt 1.
              </p>
              <div className="lt-gate-status" data-testid="retention-approval">
                <span className={retentionApproval ? 'is-ready' : ''}>{retentionApproval ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}</span>
                <div>
                  <strong>Retention-Freigabe</strong>
                  <small>{retentionApproval
                    ? `${retentionApproval.reference} · ${fmt(retentionApproval.created_at_ms)} · Freigabedatum ${retentionApproval.approval_date}`
                    : 'keine aktuelle Freigabe'}</small>
                </div>
              </div>
              <div className="lt-gate-status" data-testid="test-enforcement-status">
                <span className={config.test_enforcement_enabled ? 'is-ready' : ''}>{config.test_enforcement_enabled ? <CheckCircle2 size={15} /> : <LockKeyhole size={15} />}</span>
                <div>
                  <strong>Abweisung auf Android-Testgeräten: {config.test_enforcement_enabled ? 'aktiv' : 'off'}</strong>
                  <small>{testEnforcement?.activation_ready
                    ? 'Aktivierungsgates erfüllt — der Schalter oben ist bedienbar'
                    : 'Aktivierungsgates offen — der Schalter oben nennt, was fehlt'}</small>
                </div>
              </div>

              {canOwner && !retentionApproval && (
                <div className="lt-owner-form">
                  <strong>Schritt 1 — Retention-Freigabe auditiert erfassen</strong>
                  <small className="lt-form-hint">
                    Hier fängt alles an: Ohne diese Freigabe bleibt der Retention-Schalter gesperrt,
                    und ohne Retention bleiben alle vier Betriebsarten oben grau. Alle vier Angaben
                    sind Pflicht, danach wird der Eintrag unveränderlich.
                  </small>
                  <label><span>Freigabereferenz (Aktenzeichen)</span><input value={approvalReference} onChange={(event) => setApprovalReference(event.target.value)} /></label>
                  <label><span>Freigabedatum</span><input type="date" value={approvalDate} onChange={(event) => setApprovalDate(event.target.value)} /></label>
                  <label><span>Begründung</span><textarea value={approvalReason} maxLength={1000} onChange={(event) => setApprovalReason(event.target.value)} /></label>
                  <button type="button" className="lt-button is-primary" disabled={!ownerConfirm || !approvalReference.trim() || !approvalReason.trim() || !approvalDate} onClick={createRetentionApproval}>
                    Freigabe unveränderlich speichern
                  </button>
                  {/* Der Knopf war schon immer gesperrt, solange etwas fehlt — und das
                      Häkchen, das ihn freigibt, steht in einem anderen Block darunter. Genau
                      diese Fangstelle wird hier ausgesprochen; die Bedingung bleibt gleich. */}
                  {(!ownerConfirm || !approvalReference.trim() || !approvalReason.trim() || !approvalDate) && (
                    <LockNote testId="approval-button-lock">
                      Der Knopf bleibt gesperrt, solange etwas fehlt. Offen:{' '}
                      {[
                        !approvalReference.trim() ? 'Freigabereferenz' : null,
                        !approvalDate ? 'Freigabedatum' : null,
                        !approvalReason.trim() ? 'Begründung' : null,
                        !ownerConfirm ? 'das Häkchen „Änderung ausdrücklich bestätigen" (ganz unten in diesem Abschnitt)' : null,
                      ].filter(Boolean).join(' · ')}.
                    </LockNote>
                  )}
                </div>
              )}

              {canOwner && (
                <div className="lt-owner-confirm">
                  <strong className="lt-owner-confirm-title">Owner-Bestätigung</strong>
                  <small className="lt-form-hint">
                    Das Häkchen gilt für beides: Ohne es bleibt der Freigabe-Knopf oben gesperrt, und
                    das Einschalten von „enforce" weist der Server ohne es ab. Die Begründung hier
                    gehört zur Konfiguration — die Freigabe oben hat ihre eigene.
                  </small>
                  <label><span>Owner-Begründung</span><textarea value={ownerReason} maxLength={1000} onChange={(event) => setOwnerReason(event.target.value)} /></label>
                  <label className="lt-confirm-check">
                    <input type="checkbox" checked={ownerConfirm} onChange={(event) => setOwnerConfirm(event.target.checked)} />
                    Änderung ausdrücklich bestätigen
                  </label>
                </div>
              )}
            </section>
          </div>

          {providers && (
            <section className="lt-provider-section">
              <div className="lt-section-heading"><div><span>Produktionsbereitschaft</span><h3>Attestation-Provider</h3></div><Smartphone size={16} /></div>
              <div className="lt-provider-grid">
                {['android', 'ios'].map((platform) => <ProviderCard key={platform} platform={platform} provider={providers[platform]} />)}
              </div>
            </section>
          )}

          <footer className="lt-config-footer">
            <span><LockKeyhole size={14} /> Speichern bleibt Owner-bestätigt und revisionsgebunden.</span>
            <button type="button" className="lt-button is-primary" onClick={saveConfig}><Save size={15} /> Konfiguration auditiert speichern</button>
          </footer>
        </section>
      )}
    </div>
  );
}
